<script>
/**
 * @author lvxin08
 * 基于table-pro基础表格扩展的可编辑、校验表格
 */
import { isPlainObject, } from '@lsfe/tools';
import Validator from 'async-validator';
import Rules from '@lsfe/rules';
import emitter from '../../utils/mixins/emitter';

export default {
    name: 'XxEditTable',
    props: {
        /**
         * 提示样式
         * top: 在表格内输入框上方
         * bottom: 在表格内输入框下方
         * over: 以气泡方式浮动在输入框上方
         * toast: 输入框仅标红，通过message tost提示
         * none: 校验函数只返回结果，没有任何提示
         */
        tipStyle: {
            type: String,
            validator: value => ['top', 'bottom', 'over', 'toast', 'none'].includes(value),
            default: 'bottom'
        },
        /**
         * 校验规则 和element form组件rule一致
         * 增强：自定义 validator 添加了rowData 的回传
         */
        rules: {
            type: Object,
            default: () => ({})
        },
        /**
         * 校验前的过滤钩子函数
         */
        beforeValidate: {
            type: Function,
            default: data => data
        },
        editFlag: {
            type: [String, Boolean],
            default: false
        },
        value: {
            type: Array,
            default: () => []
        },
        columns: {
            type: Array,
            default: () => []
        },
        modelKeys: {
            type: Array,
            default: () => []
        },
        rowKey: {
            type: [Function, String],
            default: null
        },
        responseProcessor: {
            type: Function,
            default: data => data
        }
    },
    mixins: [emitter],
    data() {
        return {
            errorMap: {},
            remoteRule: {},
            editCache: {},
            localRules: {}

        };
    },
    created() {
        this._filed = {
            prop: this.$attrs.name,
            validate: async (v, callBack) => {
                const res = await this.validate(true);
                if (res) {
                    return callBack();
                }
                callBack('表格数据有误');
            }
        };

        this.dispatch('ElForm', 'el.form.addField', this._filed);

        this.localRules = this.combileRulesByColumns(this.columns || []);
    },
    beforeDestroy() {
        this.dispatch('ElForm', 'el.form.removeField', this._filed);
    },
    computed: {
        combinedRules() {
            return {
                ...this.rules,
                ...this.localRules,
                ...this.remoteRule
            };
        },
        /**
         * 构建table-pro的 scopedSlots
         * 透传所有非edit列的 slots
         * 自动添加 edit 列的 slots
         */
        scopedSlots() {
            const { $scopedSlots, tipStyle, columns } = this;
            const editorSlot = columns.filter(item => item.edit).reduce((slots, column) => {
                const options = isPlainObject(column.edit) ? column.edit : {};
                const {
                    template: Input = 'el-input', // 默认组件是el-input
                    list = [], // select、checkboxgroup radiogroup的子节点配置项统一为 {label:'文本',value:'1'}
                    attrs = {}, // 其他属性透传
                    events = {}
                } = options;

                /**
                 * radiogroup checkboxgroup  select可能还有子节点
                 */
                const child = list.map(item => {
                    const { label, value, attrs = {} } = item;
                    switch (Input) {
                        case 'el-radio-group':
                            return (
                                <el-radio
                                    label={value}
                                    {...{ attrs }}
                                >
                                    {label}
                                </el-radio>
                            );
                        case 'el-checkbox-group':
                            return (
                                <el-checkbox
                                    label={value}
                                    {...{ attrs }}
                                >
                                    {label}
                                </el-checkbox>
                            );
                        case 'el-select':
                            return (
                                <el-option
                                    label={label}
                                    value={value}
                                    {...{ attrs }}
                                />
                            );
                        default: return '';
                    }
                });

                slots[column.prop] = scope => {
                    const rowKey = this.getRowKey(scope.row) || scope.$index + scope.offset;
                    const errorMsg = this.errorMap[`${column.prop}_${rowKey}`];
                    let vnode;
                    const focus = events.focus || function () { };
                    if ($scopedSlots[column.prop]) {
                        // 如果有slot 则使用自定义slot
                        vnode = $scopedSlots[column.prop](scope);
                    } else {
                        vnode = (
                            <Input
                                size='mini'
                                class="edit-table-input"
                                key={`${column.prop}-${rowKey}`}
                                {...{
                                    attrs,
                                    on: {
                                        focus: () => focus(scope.row[column.prop], scope),
                                        input: v => scope.row[column.prop] = v,
                                        ...events,
                                        change: (v, item) => this.change(v, item, scope)
                                    }
                                }}
                                scope={scope}
                                value={scope.row[column.prop]}
                                disabled={typeof column.disabled === 'undefined' ? scope.row[`${column.prop}Disabled`] === true : column.disabled}
                            >
                                {child}
                            </Input>
                        );
                    }

                    if (this.editFlag === false || scope.row[this.editFlag]) {
                        return (
                            <div class={`input-wrapper ${tipStyle} ${errorMsg ? 'has-error' : ''}`}>
                                {vnode}
                                <div class="error-msg">
                                    {errorMsg}
                                </div>
                            </div>
                        );
                    }

                    return ($scopedSlots[column.prop] ? vnode : <span>{scope.row[column.prop]}</span>);

                };
                return slots;
            }, {});

            return {
                ...$scopedSlots,
                ...editorSlot
            };
        }
    },
    methods: {
        getRowKey(row) {
            const { rowKey } = this;
            if (!rowKey) {
                return null;
            }
            if (typeof rowKey === 'string') {
                // 支持多级.操作
                const keyList = rowKey.split('.');
                return keyList.reduce((preVal, key) => preVal && preVal[key], row);
            }
            return rowKey(row);
        },
        tableDataChange(opts) {
            const { modelKeys } = this;
            let data = this.getData();
            if (modelKeys.length) {
                // 如果配置了 modelKeys 则model数据过滤掉非指定的key
                data = data.map(item => {
                    return modelKeys.reduce((obj, key) => {
                        obj[key] = item[key];
                        return obj;
                    }, {});
                });
            }
            this.$emit('input', data);
            this.$emit('change', data, opts);
        },

        change(v, item, scope) {
            const property = scope.column.property;
            this.$set(scope.row, property, v);
            this.validateField(scope);
            const rowKey = this.getRowKey(scope.row);
            this.editCache[rowKey] = { ...scope.row };
            this.$emit('editChange', v, item, scope);
            this.tableDataChange({
                __index: scope.$index,
                value: v,
                __type: 'edit',
                changeField: property,
                item: item,
                row: scope.row
            });
        },

        clear() {
            this.errorMap = {};
        },

        clearEdit() {
            this.editCache = {};
            this.clear();
        },

        /**
         * 校验单个字段
         */
        validateField(scope) {
            const { column: { property }, offset, $index, row } = scope;
            const { combinedRules } = this;
            if (!combinedRules[property]) {
                return Promise.resolve(true);
            }
            const validator = new Validator({ [property]: combinedRules[property] });
            return new Promise(reslove => {
                validator.validate(row, { firstFields: true }, errors => {
                    const rowKey = this.getRowKey(scope.row) || offset + $index;
                    if (errors) {
                        const errorMsg = errors[0] && errors[0].message || '';
                        this.$set(this.errorMap, `${property}_${rowKey}`, errorMsg);
                        if (this.tipStyle === 'toast') {
                            this.$message.error(errorMsg);
                        }
                        return reslove(false);
                    }
                    this.$set(this.errorMap, `${property}_${rowKey}`, '');
                    reslove(true);
                });
            });
        },

        /**
         * 校验单行
         */
        async validateRow(scope) {
            const { combinedRules } = this;
            const { pageSize, currentPage } = this.$refs.table.paginationDef;
            const offset = (currentPage - 1) * pageSize;
            const validator = new Validator(combinedRules);
            const rowKey = this.getRowKey(scope.row) || offset + scope.$index; // 没有配置rowKey则使用行索引
            return new Promise(resolve => {
                validator.validate(scope.row, { firstFields: true }, errors => {
                    if (!errors) {
                        return resolve(true);
                    }

                    // errors
                    errors.forEach(error => {
                        this.$set(this.errorMap, `${error.field}_${rowKey}`, error.message || '');
                    });

                    if (this.tipStyle === 'toast') {
                        this.$message.error({
                            message: `
                                ${[...new Set(errors.map(err => err.message))].join('<br/>')}
                            `,
                            dangerouslyUseHTMLString: true
                        });
                    }

                    return resolve(false);
                });
            });
        },

        /**
        * 全部校验
        */
        async validate(validateAll) {
            const { $attrs: { source = 'remote' } } = this;
            const { combinedRules } = this;
            let data = this.$refs.table.curTableData;
            const { pageSize, currentPage } = this.$refs.table.paginationDef;
            let offset = (currentPage - 1) * pageSize;

            if (!Object.keys(combinedRules).length) {
                return true;
            }

            if (validateAll === true) {
                // 只有显式指定 validateAll 为true 执行全表数据校验
                // 默认只校验当前页
                offset = 0;
                data = source === 'local' ? this.$refs.table.localData : Object.values(this.editCache);
            }

            const validator = new Validator(combinedRules);
            data = await this.beforeValidate(data);

            const task = data.map((row, index) => new Promise(resolve => {

                const rowKey = this.getRowKey(row) || offset + index; // 没有配置rowKey则使用行索引
                validator.validate(row, { firstFields: true }, errors => {
                    if (!errors) {
                        return resolve(null);
                    }

                    // index：错误数据的行索引 有分页的话要加上分页
                    resolve({
                        index: rowKey,
                        focusIndex: offset + index,
                        errors
                    });
                });
            }));

            const validateRes = await Promise.all(task);
            const errorList = validateRes.filter(err => err);

            this.errorMap = errorList.reduce((map, rowError) => {
                rowError.errors.forEach(error => {
                    map[`${error.field}_${rowError.index}`] = error.message || '';
                });
                return map;
            }, {});

            this.focusError(errorList[0] && errorList[0].focusIndex || 0);
            if (this.tipStyle === 'toast' && errorList.length) {
                // toast 提示错误
                this.$message.error({
                    message: `
                        ${[...new Set(Object.values(this.errorMap))].join('<br/>')}
                    `,
                    dangerouslyUseHTMLString: true
                });
            }
            return errorList.length === 0;
        },

        pageChange(val) {
            this.$emit('current-page-change', val);
        },

        /**
         * 定位错误
         */
        async focusError(rowIndex) {
            const { $attrs: { source = 'remote' } } = this;
            const table = this.$refs.table;
            if (table) {
                const { pageSize } = table.paginationDef;
                if (source === 'local') {
                    const page = Math.floor(rowIndex / pageSize) + 1;
                    table.handleCurrentChange(page);
                }

                await this.$nextTick();

                const el = table.$el.querySelector('.input-wrapper.has-error');
                if (el) {
                    el.scrollIntoView();
                }
            }
        },
        fetchData(...args) {
            return this.$refs.table.fetchData(...args);
        },

        remoteDataChange({ config = {} }) {
            // 提取配置了 rule 的 column
            this.remoteRule = this.combileRulesByColumns(config.columns);

            this.tableDataChange();

        },

        combileRulesByColumns(columns) {
            let rules = (columns || []).reduce((rules, column) => {
                let rule = column.rule || column.rules;
                if (rule) {
                    rules[column.prop] = rule.map(opt => {
                        const { method, options = {} } = opt;
                        const { len, min, max } = options;
                        const numExp = /^(-|\+)?\d+(\.\d+)?$/;
                        const minIsNum = numExp.test(min);
                        const maxIsNum = numExp.test(max);
                        let message = opt.message;

                        if (!message) {
                            // 如果没有指定message ，则处理默认的message信息
                            switch (method) {
                                case 'isRequired':
                                    message = `请填写${column.label}`;
                                    break;
                                case 'typeValid':
                                    // 使用 @lsfe/rules 默认提示
                                    break;
                                case 'decimalLen':
                                    if (+len > 0) {
                                        message = `请填写${len}位小数`;
                                    }
                                    break;
                                case 'range':
                                    if (minIsNum && !maxIsNum) {
                                        message = `请填写大于${min}的数`;
                                    } else if (!minIsNum && maxIsNum) {
                                        message = `请填写小于${max}的数`;
                                    } else {
                                        message = `请填写${min} - ${max}的数`;
                                    }
                                    break;
                                default:
                                    message = '填写信息有误';
                            }
                        }

                        return Rules[method](options, message);
                    });
                }
                return rules;
            }, {});
            return rules;
        },

        getData() {
            const { $attrs: { source = 'remote' } } = this;
            return source === 'local' ? this.$refs.table.localData : Object.values(this.editCache);
        },

        getRrferData() {
            const { $attrs: { source = 'remote' } } = this;
            return source === 'local' ? this.$refs.table.localData : this.$refs.table.remoteData;
        },

        addRow(row, head = true) {
            // head 表示插入的是头部
            const data = this.getRrferData();
            const method = head ? 'unshift' : 'push';
            if (Array.isArray(row)) {
                Array.prototype[method].apply(data, row);
            } else {
                data[method](row);
            }
            this.tableDataChange({
                __index: data.length - 1,
                __type: 'add',
                row: row
            });
            this.clear();
        },

        deleteRow(row, find) {
            const data = this.getRrferData();
            const index = data.findIndex(find || (item => {
                if (this.getRowKey(item)) {
                    return this.getRowKey(item) === this.getRowKey(row);
                }
                return item === row;
            }));

            if (index > -1) {
                data.splice(index, 1);
                // 清除编辑缓存
                delete this.editCache[this.getRowKey(row)];
                this.tableDataChange({
                    __index: index,
                    __type: 'delete',
                    row: row
                });
                // 清除错误信息
                this.clear();
                const { curTableData } = this.$refs.table;
                if (!curTableData.length) {
                    // 当前页数据被清空时  需要跳到前一页
                    const pagingData = this.invoke('getPagingData', true);
                    if (pagingData.currentPage > 1) {
                        this.invoke('handleCurrentChange', pagingData.currentPage - 1);
                    }

                }
            }
        },

        async innerResponseProcessor(res) {
            const tableData = await this.responseProcessor(res);
            const data = (tableData.data || []).map(row => {
                const rowKey = this.getRowKey(row);
                if (this.editCache[rowKey]) {
                    return { ...this.editCache[rowKey] };
                }
                return row;
            });

            return {
                ...tableData,
                data
            };
        },

        /**
         * 执行底层 table-pro的方法
         */
        invoke(method, ...args) {
            return this.$refs.table[method](...args);
        }
    },
    render(h) {
        return (
            <xx-table-pro
                {...{
                    attrs: { ...this.$attrs, columns: this.columns, responseProcessor: this.innerResponseProcessor, rowKey: this.rowKey },
                    on: {
                        ...this.$listeners,
                        'current-page-change': this.pageChange,
                        remoteDataChange: this.remoteDataChange
                    },
                    scopedSlots: this.scopedSlots
                }}
                class="edit-table"
                ref="table"
            ><template slot="prefix">{this.$slots.prefix}</template></xx-table-pro>
        );
    }
};
</script>
<style lang="scss" scoped>
.edit-table {
    /deep/ {
        .cell {
            overflow: visible;
        }
    }
}

.edit-table-input {
    /deep/ .el-input__inner {
        padding: 0 3px;
    }
}

.input-wrapper {
    position: relative;
    padding: 16px 0;
    overflow: visible;
    // border: 1px solid red;

    .error-msg {
        position: absolute;
        display: none;
        color: #f56c6c;
        line-height: 1;
        overflow: visible;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 12px;
        bottom: -2px;
        left: 0;
    }

    &.has-error {
        &:not(.none) /deep/ .el-input__inner {
            border-color: #f56c6c;
        }
        .error-msg {
            display: block;
        }
    }

    &.top {
        .error-msg {
            bottom: auto;
            top: -8px;
        }
    }

    .triangle {
        position: absolute;
        display: block;
        content: "";
        width: 0;
        height: 0;
        left: 30px;
        border: 5px solid #f56c6c;
        border-left-color: transparent;
        border-right-color: transparent;
        border-bottom-color: transparent;
    }

    &.over {
        padding: 0;
        .error-msg {
            background: #f56c6c;
            color: #fff;
            border: 1px solid #f56c6c;
            border-radius: 3px;
            line-height: 1;
            top: -10px;
            bottom: auto;
            padding: 1px 5px;
            transform: scale(0.8);
            &:before {
                @extend .triangle;
                bottom: -10px;
            }
            // &:after {
            //     @extend .triangle;
            //     bottom: -9px;
            //     border-top-color: #fff;
            // }
        }
    }

    &.none,
    &.toast {
        .error-msg {
            display: none;
        }
    }
}
</style>
